import { parseResultTime, type RideTiming } from '../../../../../services/simulation/runReport';
import type { RoadRouteWithMetadata } from '../../../../../services/simulation/resultsPathsService';
import type { LatLngTuple } from 'leaflet';
import type { ActivityTimes, PlaybackSegmentInput } from '../../../../../utils/simulation/vehiclePlayback';
import type { SimulationActivity, UnmatchedRide } from '../../../../../types/simulation';

export const RECENT_TRIP_WINDOW_MS = 45 * 60_000;
/** A stop visit spans a few activity numbers; a wider range is bad data, not a stop. */
const MAX_ACTIVITY_RANGE = 1000;

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export interface MapTrip {
  readonly rideId: string;
  readonly shiftId: string;
  readonly pickMs: number;
  readonly dropMs: number;
  /** Pickup to drop-off. Built once per run so map lines keep the same positions between frames. */
  readonly line: LatLngTuple[];
}

export interface MapRequest {
  readonly key: string;
  readonly requestMs: number | null;
  readonly at: LatLngTuple;
}

/** Road legs for playback, one per shift and segment, with riders counted once although `/paths` lists each under two ids. */
export function playbackSegments(routes: Readonly<Record<string, RoadRouteWithMetadata>>, rides: readonly RideTiming[]): PlaybackSegmentInput[] {
  const canonical = new Map<string, string>();
  for (const ride of rides) {
    canonical.set(ride.rideId, ride.rideId);
    if (ride.externalReferenceId) canonical.set(ride.externalReferenceId, ride.rideId);
  }
  const seen = new Set<string>();
  const segments: PlaybackSegmentInput[] = [];
  for (const route of Object.values(routes)) {
    const leg = route.metadata;
    if (!route.geometry || !leg?.vehicle_shift_id || leg.segment_index == null) continue;
    const key = `${leg.vehicle_shift_id}:${leg.segment_index}`;
    if (seen.has(key)) continue;
    seen.add(key);
    segments.push({
      coordinates: route.geometry.coordinates,
      vehicleShiftId: leg.vehicle_shift_id,
      fromActivityNumber: leg.from_driver_activity_number,
      toActivityNumber: leg.to_driver_activity_number,
      onboardCount: new Set((leg.rides_onboard_ids ?? []).map((id) => canonical.get(id) ?? id)).size,
    });
  }
  return segments;
}

/** Start and end of every driver activity number, per shift; a stop visit covers a range of numbers. */
export function activityTimes(activities: readonly SimulationActivity[]): ActivityTimes {
  const byShift: ActivityTimes = new Map();
  for (const activity of activities) {
    const shiftId = activity.vehicle_shift_id ?? activity.shift_id;
    const startMs = parseResultTime(activity.activity_start_time);
    if (!shiftId || startMs == null) continue;
    const bounds = { startMs, endMs: parseResultTime(activity.activity_end_time) ?? startMs };
    const numbers = byShift.get(shiftId) ?? new Map<number, { startMs: number; endMs: number }>();
    byShift.set(shiftId, numbers);
    const set = (number: number | undefined) => {
      if (isNumber(number)) numbers.set(number, bounds);
    };
    set(activity.driver_activity_number);
    const low = activity.min_driver_activity_number ?? activity.max_driver_activity_number;
    const high = activity.max_driver_activity_number ?? activity.min_driver_activity_number;
    if (!isNumber(low) || !isNumber(high)) continue;
    const from = Math.min(low, high);
    const to = Math.max(low, high);
    if (to - from > MAX_ACTIVITY_RANGE) continue;
    for (let number = from; number <= to; number += 1) set(number);
  }
  return byShift;
}

/** Every trip with a pickup and drop-off location. */
export function allTrips(rides: readonly RideTiming[]): MapTrip[] {
  return rides.flatMap((ride) =>
    ride.from && ride.to
      ? [{ rideId: ride.rideId, shiftId: ride.shiftId, pickMs: ride.pickMs, dropMs: ride.dropMs, line: [[ride.from[0], ride.from[1]], [ride.to[0], ride.to[1]]] }]
      : []
  );
}

/** Trips on board at t, and trips dropped off in the window before t. */
export function tripsAt(trips: readonly MapTrip[], atMs: number): { inProgress: MapTrip[]; recent: MapTrip[] } {
  return {
    inProgress: trips.filter((trip) => trip.pickMs <= atMs && atMs < trip.dropMs),
    recent: trips.filter((trip) => trip.dropMs <= atMs && atMs - trip.dropMs <= RECENT_TRIP_WINDOW_MS),
  };
}

/** Unmatched requests that have a pickup location. */
export function unmatchedRequests(rides: readonly UnmatchedRide[]): MapRequest[] {
  return rides.flatMap((ride, index) => {
    const lat = ride.from_latitude;
    const lng = ride.from_longitude;
    if (!isNumber(lat) || !isNumber(lng)) return [];
    return [{ key: ride.ride_id ?? `unmatched-${index}`, requestMs: parseResultTime(ride.request_time), at: [lat, lng] }];
  });
}

/** Requests made by t, or every request when t is null. */
export function requestsBy(requests: readonly MapRequest[], atMs: number | null): readonly MapRequest[] {
  return atMs == null ? requests : requests.filter((request) => request.requestMs != null && request.requestMs <= atMs);
}
